import { basicInfoFormSchema } from './validation';
import { OfferData } from '../types/offers';
import {
  CertificateDto,
  CertificateTypeDto,
  DocumentDto,
} from '../types/agency';
import { Certification } from '../components/Modals/CertificationModal';
import toast from 'react-hot-toast';
import * as tus from 'tus-js-client';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SelectOption } from '../types/app';
import axios from 'axios';
import { CountryOption } from '../service/api/location';

export const formatDate = (
  date: string | Date | number | null | undefined,
  fallback: string = '',
): string => {
  if (!date) return fallback;

  let dateToFormat: Date;

  if (typeof date === 'string' || typeof date === 'number') {
    dateToFormat = new Date(date);
  } else if (date instanceof Date) {
    dateToFormat = date;
  } else {
    return fallback; // Handle cases where date is null or undefined after the initial check
  }

  return dateToFormat.toLocaleDateString('default', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const isEmailFormat = (value: any) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

export const scrollBarStyle = {
  '&::-webkit-scrollbar': {
    width: '11px',
    height: '6px',
    borderRadius: '20px',
    backgroundColor: '#D9D9D9',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '20px',
    backgroundColor: '#FF9D16',
  },
};

export const scrollBarStyls = {
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    borderRadius: '11px',
    backgroundColor: '#D9D9D9',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '11px',
    backgroundColor: '#FF9D16',
  },
};

export const yearOptions = Array.from(
  { length: new Date().getFullYear() - 1900 + 1 },
  (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  },
);

export const addEmploymentDetail = (values: any) => ({
  ...values,
  employmentDetails: [
    ...values.employmentDetails,
    {
      jobTitle: '',
      company: '',
      domain: '',
      techStack: [],
      startDate: '',
      endDate: '',
      isCurrentlyWorking: false,
      description: '',
    },
  ],
});

export const removeEmploymentDetail = (values: any, index: number) => ({
  ...values,
  employmentDetails: values.employmentDetails.filter(
    (_: null, i: any) => i !== index,
  ),
});

export const addCertification = (values: any) => ({
  ...values,
  professionalCertifications: [
    ...values.professionalCertifications,
    {
      certificateName: '',
      validationUrl: '',
      certificateFile: null,
      issueDate: '',
      expirationDate: '',
    },
  ],
});

export const removeCertification = (values: any, index: any) => ({
  ...values,
  professionalCertifications: values.professionalCertifications.filter(
    (_: null, i: number) => i !== index,
  ),
});

// Additional validation helpers
export const validateStep = (step: any, values: any) => {
  switch (step) {
    case 1:
      return basicInfoFormSchema
        .pick([
          'firstName',
          'lastName',
          'gender',
          'professionalTitle',
          'emailAddress',
          'country',
          'city',
          'phoneNumber',
          'aboutMe',
          'languageProficiency',
          'professionalPhoto',
        ])
        .validate(values);
    case 2:
      return basicInfoFormSchema.pick(['employmentDetails']).validate(values);
    case 3:
      return basicInfoFormSchema
        .pick(['professionalCertifications'])
        .validate(values);
    default:
      return basicInfoFormSchema.validate(values);
  }
};

export const formatCurrency = (amount: number) => {
  return `USD ${amount.toLocaleString()}`;
};

export const groupOffersByDay = (offers: OfferData[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

  const grouped: Record<string, OfferData[]> = {};

  offers.forEach((offer) => {
    const receivedDate = new Date(offer.received);
    const offerDate = new Date(
      receivedDate.getFullYear(),
      receivedDate.getMonth(),
      receivedDate.getDate(),
    );

    let groupKey: string;

    if (offerDate.getTime() === today.getTime()) {
      groupKey = 'Today';
    } else if (offerDate.getTime() === yesterday.getTime()) {
      groupKey = 'Yesterday';
    } else {
      groupKey = receivedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    grouped[groupKey].push(offer);
  });

  return grouped;
};

// Helper function to sort group keys
export const sortGroupKeys = (keys: string[]) => {
  const priority = { Today: 3, Yesterday: 2 };

  return keys.sort((a, b) => {
    const aPriority = priority[a as keyof typeof priority] || 1;
    const bPriority = priority[b as keyof typeof priority] || 1;

    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }

    if (aPriority === 1 && bPriority === 1) {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    }

    return 0;
  });
};

export const paginateGroupedOffers = (
  groupedOffers: Record<string, OfferData[]>,
  sortedKeys: string[],
  perPage: number,
) => {
  const pages: Array<
    Array<{ groupKey: string; offer?: OfferData; isHeader?: boolean }>
  > = [];
  let currentPage: Array<{
    groupKey: string;
    offer?: OfferData;
    isHeader?: boolean;
  }> = [];
  let currentOfferCount = 0;

  for (const groupKey of sortedKeys) {
    const offers = groupedOffers[groupKey];
    let offerIndex = 0;

    while (offerIndex < offers.length) {
      // If starting a new page or new group, add header
      if (
        currentOfferCount === 0 ||
        (currentPage.length > 0 &&
          !currentPage.some(
            (item) => item.groupKey === groupKey && item.isHeader,
          ))
      ) {
        currentPage.push({ groupKey, isHeader: true });
      }

      // Calculate how many offers we can fit in current page
      const spaceLeft = perPage - currentOfferCount;
      const offersToAdd = offers.slice(offerIndex, offerIndex + spaceLeft);

      // Add offers to current page
      offersToAdd.forEach((offer) => {
        currentPage.push({ groupKey, offer });
      });

      offerIndex += offersToAdd.length;
      currentOfferCount += offersToAdd.length;

      // If page is full, start new page
      if (currentOfferCount === perPage) {
        pages.push(currentPage);
        currentPage = [];
        currentOfferCount = 0;
      }
    }
  }

  // Add remaining page if it has content
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
};

export const removeLanguage = (values: any, index: number) => {
  const updatedLanguages = values.languageProficiency.filter(
    (_: any, i: number) => i !== index,
  );
  return { ...values, languageProficiency: updatedLanguages };
};

export function formatToIncDate(dateInput: string) {
  const date = new Date(dateInput);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00Z`;
}

export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const formatToInputDate = (ddmmyyyy: string | undefined): string => {
  if (!ddmmyyyy) return '';
  if (ddmmyyyy.toLowerCase() === 'present') return '';

  // Handle already converted YYYY-MM-DD format
  if (ddmmyyyy.includes('-') && ddmmyyyy.split('-')[0].length === 4) {
    return ddmmyyyy;
  }

  const parts = ddmmyyyy.split('/');

  if (parts.length !== 3) {
    console.error('Invalid DD/MM/YYYY format:', ddmmyyyy);
    return '';
  }

  const [day, month, year] = parts;

  if (!day || !month || !year) {
    console.error('Invalid DD/MM/YYYY format:', ddmmyyyy);
    return '';
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};
export const splitPhoneNumber = (fullNumber: string, localCountries: any[]) => {
  if (!fullNumber || !fullNumber.startsWith('+')) {
    return { code: '+1', digits: fullNumber };
  }

  const allCountryCodes = localCountries
    .map((c: any) => c.dial_code)
    .filter((c: string) => c);

  // Find the longest matching code
  const foundCode = allCountryCodes
    .filter((c: string) => fullNumber.startsWith(c))
    .sort((a: string, b: string) => b.length - a.length)[0];

  if (foundCode) {
    const digits = fullNumber.substring(foundCode.length);
    return { code: foundCode, digits: digits };
  }

  return { code: '+1', digits: fullNumber };
};

export const getLanguageName = (id: string | number, languages: any) => {
  if (!languages) return String(id);
  const lang = languages.find((l: any) => String(l.id) === String(id));
  return lang ? lang.name : String(id);
};

export const formatToDDMMYYYY = (
  value: string | Date | null | undefined,
): string | undefined => {
  if (!value) {
    console.warn('formatToDDMMYYYY: No value provided');
    return undefined;
  }

  if (typeof value === 'string' && value.toLowerCase() === 'present') {
    return 'Present';
  }

  let date: Date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    // If it's already in DD/MM/YYYY format, return it
    if (value.includes('/') && value.split('/').length === 3) {
      const [day, month, year] = value.split('/');
      if (day && month && year && year.length === 4) {
        return value; // Already formatted
      }
    }

    // Handle YYYY-MM-DD format (from calendar/Formik state)
    if (value.includes('-') && value.split('-')[0].length === 4) {
      const [year, month, day] = value.split('-');
      // Create date using local timezone components to avoid off-by-one errors
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      date = new Date(value);
    }
  } else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) {
    console.error('formatToDDMMYYYY received an invalid date value:', value);
    return undefined;
  }

  // Use local date components to avoid timezone shifts
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const result = `${day}/${month}/${year}`;
  console.log('formatToDDMMYYYY converted:', value, '→', result);
  return result;
};

export const safeDateConvert = (dateStr: any) => {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD string
  }
  return dateStr; // Return as is if already in ISO format
};

export const formatDDMMYYYYToDisplay = (
  ddmmyyyy: string | undefined,
): string => {
  if (!ddmmyyyy) return '';

  const [day, month, year] = ddmmyyyy.split('/');

  // Create date using local timezone
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  if (isNaN(date.getTime())) {
    console.error('Invalid DD/MM/YYYY format:', ddmmyyyy);
    return ddmmyyyy; // Return original if invalid
  }

  return date.toLocaleDateString('default', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatAnyDateToDisplay = (
  dateString: string | undefined,
): string => {
  if (!dateString) return '';

  let date: Date;

  // Handle DD/MM/YYYY format
  if (dateString.includes('/')) {
    const [day, month, year] = dateString.split('/');
    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  // Handle YYYY-MM-DD format
  else if (dateString.includes('-')) {
    const parts = dateString.split('-');
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const [year, month, day] = parts;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      // DD-MM-YYYY (just in case)
      const [day, month, year] = parts;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  } else {
    console.error('Unsupported date format:', dateString);
    return dateString;
  }

  if (isNaN(date.getTime())) {
    console.error('Invalid date:', dateString);
    return dateString;
  }

  return date.toLocaleDateString('default', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const mapCertificationToDto = async (
  cert: Certification,
): Promise<Partial<CertificateDto> | undefined> => {
  if (!cert.certificateName && !cert.certLink && !cert.certificateFile) {
    return undefined;
  }

  let certDocumentDto: DocumentDto | undefined = undefined;

  // 🚀 Check if we have an existing document first
  if (!(cert.certificateFile instanceof File)) {
    certDocumentDto = {
      documentUrl: cert.certificateFile?.documentUrl,
      name: cert.certificateFile?.name,
    }; // Use existing document
  } else if (cert.certificateFile instanceof File) {
    // Only process new file if no existing document
    try {
      const file64 = await convertToBase64(cert.certificateFile);
      certDocumentDto = {
        base64Str: file64,
        name: cert.certificateFile.name,
        fileType: cert.certificateFile.type,
        contentSize: cert.certificateFile.size,
        isChanged: true,
      } as DocumentDto;
    } catch (e) {
      toast.error(
        `Failed to process certification file: ${cert.certificateFile.name}`,
      );
      return undefined;
    }
  }

  const mtCertDto = cert.mtCertDto
    ? cert.mtCertDto
    : cert.certificateId
      ? ({ id: Number(cert.certificateId) } as CertificateTypeDto)
      : undefined;

  return {
    id: cert.id,
    certificateName: cert.certificateName,
    certLink: cert.certLink,
    issueDate: formatToDDMMYYYY(cert.issueDate),
    expirationDate: cert.expirationDate
      ? formatToDDMMYYYY(cert.expirationDate)
      : undefined,
    certDto: certDocumentDto,
    mtCertDto: mtCertDto,
  };
};

export const initiateVimeoTusUpload = (
  file: File,
  uploadLink: string,
  associateId: number | null,
  updateProgress: (p: number) => void,
  onSuccess: (uploadUrl: string) => void,
  onError: (error: Error) => void,
) => {
  // 1. Setup the uploader instance
  const upload = new tus.Upload(file, {
    endpoint: uploadLink,

    // Use the upload link provided by the backend (Vimeo)
    uploadUrl: uploadLink,

    // Headers and metadata are often required for Vimeo/TUS
    metadata: {
      filename: file.name,
      filetype: file.type,
      ...(associateId ? { associateId: String(associateId) } : {}),
    },

    // 2. Define Callbacks

    onError: function (error: Error) {
      console.error('TUS Upload failed:', error);
      onError(error);
    },

    onProgress: function (bytesUploaded, bytesTotal) {
      const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
      updateProgress(parseInt(percentage));
    },

    onSuccess: function () {
      // TUS upload is 100% complete.

      // FIX: Check for null before passing the URL to satisfy TS compiler (string | null)
      if (upload.url) {
        console.log('TUS Upload finished. File located at:', upload.url);
        onSuccess(upload.url);
      } else {
        console.error('TUS Upload finished, but final URL is missing.');
        onError(
          new Error('Final Vimeo URL is missing after successful transfer.'),
        );
      }
    },
  });

  // 3. Start the upload process
  upload.start();

  // Return the uploader instance so we can cancel it if needed
  return upload;
};

export const safeParse = (val: any) => {
  if (val === '' || val === null || val === undefined) return 0;
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

export const PerformClientSideLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Remove all queries from the cache
  queryClient.removeQueries();

  // Cancel any ongoing queries
  queryClient.cancelQueries();
  // Reset the query client to clear any in-memory cache
  queryClient.clear();

  setTimeout(() => {
    sessionStorage.removeItem('user');
    navigate('/login');
  }, 500);
};

export const getTodayString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// FIX 1: Add safe check for items array
export const mapApiToSelectOptions = (
  items: any[] | undefined | null,
  valueKey: string,
  labelKey: string,
): SelectOption[] => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    value: String(item[valueKey]), // Ensure value is always a string
    label: item[labelKey],
  }));
};

export async function downloadFromUrl(url: string, filename: string) {
  const response = await axios.get(url, {
    responseType: 'blob',
  });

  const blobUrl = URL.createObjectURL(response.data);

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(blobUrl);
  //Handle Errors at call site
}

export const onlyNumbers = (value: string) => value.replace(/\D/g, '');

export const formatCardNumber = (value: string) =>
  onlyNumbers(value).slice(0, 16);

export const formatCVC = (value: string) => onlyNumbers(value).slice(0, 3);

export const formatExpiry = (value: string) => {
  const cleaned = onlyNumbers(value).slice(0, 4);

  if (cleaned.length <= 2) return cleaned;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
};

export const formatZipCode = (value: string) => onlyNumbers(value).slice(0, 10);

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';

  // If less than 1MB, show in KB with 2 decimals
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }

  // Show in MB with 2 decimals
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};


export  const getPhoneCode = ({
        agencyDetails,
        countryData,
        countryOptions,
        userLocationData,
      }: {
        agencyDetails?: {
          phoneNumber?: string;
          regCountry?: string | number;
        };
        countryData: any[];
        countryOptions: CountryOption[];
        userLocationData?: { country_calling_code?: string };
      }) => {
        if (agencyDetails?.phoneNumber) {
          return splitPhoneNumber(agencyDetails.phoneNumber, countryData).code;
        }

        if (agencyDetails?.regCountry) {
          const matchedCode = countryOptions.find(
            (option) => String(option.value) === String(agencyDetails.regCountry)
          )?.code;

          if (matchedCode) return matchedCode;
        }

        return userLocationData?.country_calling_code || '+1';
      };
